import Vue from "vue";
import Vuex from "vuex";
import { Message, MessageBox } from "element-ui";
import api from "@/api";
import router, {
  getRegisteredRoutePaths,
  resetRouter,
  resolveDefaultPath,
} from "@/router";
import { AUTH_ROUTE_FALLBACK } from "@/constants/auth";
import { clearAuthState, getStoredAuthState, persistAuthState } from "@/utils/auth";
import { findFirstMenuPath, findMenuByPath, normalizeMenuTree } from "@/utils/menu";
import { hasAnyPermission, hasPermission } from "@/utils/permission";
import { registerDynamicRoutes, resetRouter as resetDynamicRouter } from "@/router";

Vue.use(Vuex);

function createDefaultState() {
  return {
    token: "",
    adminInfo: {},
    menuTree: [],
    permissionKeys: [],
    defaultPath: AUTH_ROUTE_FALLBACK,
    routeReady: false,
    menuList: [],
    appList: [],
    navText: "",
    baseUrl: "",
    menuId: "",
    childMenu: [],
    menuPathList: [],
    parentTitle: "",
    menu_path: "",
    nickname: "",
    navList: [],
  };
}

function buildNavItem(menuTree, path) {
  const menu = findMenuByPath(menuTree, path);
  if (!menu) {
    return null;
  }

  return {
    path,
    title: menu.title,
    navText: menu.navText,
  };
}

const store = new Vuex.Store({
  state: createDefaultState(),
  mutations: {
    setAuthState(state, payload = {}) {
      state.token = payload.token || "";
      state.adminInfo = payload.adminInfo || {};
      state.menuTree = normalizeMenuTree(payload.menuTree || []);
      state.permissionKeys = Array.isArray(payload.permissionKeys)
        ? payload.permissionKeys
        : [];
      state.defaultPath = payload.defaultPath || AUTH_ROUTE_FALLBACK;
      state.menuList = state.menuTree;
      state.nickname = state.adminInfo.nickname || "";
      state.navList = Array.isArray(payload.navList) ? payload.navList : [];
      persistAuthState({
        token: state.token,
        adminInfo: state.adminInfo,
        menuTree: state.menuTree,
        permissionKeys: state.permissionKeys,
        defaultPath: state.defaultPath,
        navList: state.navList,
      });
    },
    resetAuthState(state) {
      const nextState = createDefaultState();
      Object.keys(nextState).forEach((key) => {
        state[key] = nextState[key];
      });
      clearAuthState();
    },
    setRouteReady(state, ready) {
      state.routeReady = ready;
    },
    setNavList(state, data) {
      state.navList = data;
      persistAuthState({
        token: state.token,
        adminInfo: state.adminInfo,
        menuTree: state.menuTree,
        permissionKeys: state.permissionKeys,
        defaultPath: state.defaultPath,
        navList: state.navList,
      });
    },
    addNavItem(state, data) {
      if (!data || !data.path) {
        return;
      }

      const exists = state.navList.find((item) => item.path === data.path);
      if (!exists) {
        state.navList.push(data);
        persistAuthState({
          token: state.token,
          adminInfo: state.adminInfo,
          menuTree: state.menuTree,
          permissionKeys: state.permissionKeys,
          defaultPath: state.defaultPath,
          navList: state.navList,
        });
      }
    },
    setMenuPath(state, data) {
      state.menu_path = data;
    },
    setMenuPathList(state, data) {
      state.menuPathList = data;
    },
    setParentTitle(state, data) {
      state.parentTitle = data;
    },
    setchildMenu(state, data) {
      state.childMenu = data;
    },
    setMenuList(state, data) {
      state.menuList = data;
    },
    setAppList(state, data) {
      state.appList = data;
    },
    setNavText(state, data) {
      state.navText = data;
    },
    setBaseUrl(state, data) {
      state.baseUrl = data;
    },
    setMenuId(state, data) {
      state.menuId = data;
    },
    setNickname(state, data) {
      state.nickname = data;
    },
  },
  actions: {
    hydrateAuth({ commit, dispatch }) {
      const stored = getStoredAuthState();
      if (!stored.token) {
        commit("resetAuthState");
        return false;
      }

      commit("setAuthState", stored);
      commit("setRouteReady", false);
      dispatch("syncCurrentRouteMeta", router.currentRoute.path);
      return true;
    },
    loginSuccess({ commit, dispatch }, payload = {}) {
      commit("setAuthState", {
        token: payload.token,
        adminInfo: payload.adminInfo,
        menuTree: payload.menuTree,
        permissionKeys: payload.permissionKeys,
        defaultPath: payload.defaultPath,
      });
      commit("setRouteReady", false);
      dispatch("syncCurrentRouteMeta", payload.defaultPath);
    },
    async refreshMenuTree({ commit, state, dispatch }) {
      if (!state.token) {
        return [];
      }

      const cachedMenuTree = normalizeMenuTree(state.menuTree || []);
      let nextMenuTree = cachedMenuTree;

      try {
        const res = await api.getMenu.getMenuList();
        const remoteMenuTree = normalizeMenuTree(res.data || []);

        // 普通管理员刷新时如果菜单接口临时返回空数组，沿用登录时缓存的菜单，避免被误踢回登录页。
        if (remoteMenuTree.length > 0 || cachedMenuTree.length === 0) {
          nextMenuTree = remoteMenuTree;
        }
      } catch (error) {
        // 菜单刷新失败时保留本地菜单，避免浏览器刷新直接丢登录态。
        nextMenuTree = cachedMenuTree;
      }

      const nextDefaultPath = resolveDefaultPath(
        nextMenuTree,
        state.defaultPath
      );
      const currentPath = router.currentRoute.path;

      const nextNavList = state.navList.filter((item) =>
        getRegisteredRoutePaths(nextMenuTree).includes(item.path)
      );

      commit("setAuthState", {
        token: state.token,
        adminInfo: state.adminInfo,
        menuTree: nextMenuTree,
        permissionKeys: state.permissionKeys,
        defaultPath: nextDefaultPath,
        navList: nextNavList,
      });

      resetDynamicRouter();
      registerDynamicRoutes(nextMenuTree);
      commit("setRouteReady", true);
      dispatch("syncCurrentRouteMeta", currentPath);

      return nextMenuTree;
    },
    logout({ commit }) {
      resetRouter();
      commit("resetAuthState");
      commit("setRouteReady", false);
    },
    async logoutWithConfirm({ dispatch }) {
      return MessageBox.confirm("是否退出登录？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => {
          dispatch("logout");
          Message.success("退出成功");
          router.replace("/login");
        })
        .catch(() => { });
    },
    syncCurrentRouteMeta({ commit, state }, path) {
      const resolvedPath = path || state.defaultPath;
      const navItem = buildNavItem(state.menuTree, resolvedPath);
      if (navItem) {
        commit("addNavItem", navItem);
        commit("setNavText", navItem.navText);
        commit("setMenuPath", navItem.path);
      }
    },
    getMenuList({ commit, state }) {
      commit("setMenuList", state.menuTree);
    },
    getAllMenuList(context) {
      api.getMenu.getAllMenuList().then((res) => {
        if (res.code == 200) {
          context.commit("setMenuPathList", res.data);
        }
      });
    },
    getAppList(context) {
      api.getApp.getAppList({ page: 1, page_size: 10000 }).then((res) => {
        if (res.code == 200) {
          let data = (res.data.list || []).map((item) => {
            return {
              title: item.app_name || item.app_code,
              ...item,
            };
          });
          context.commit("setAppList", data);
        }
      });
    },
    getBaseUrl(context) {
      api.getConfig.getConfig({ iden: "web_url" }).then((res) => {
        if (res.code == 200) {
          context.commit("setBaseUrl", res.data);
        }
      });
    },
  },
  getters: {
    isLoggedIn(state) {
      return Boolean(state.token);
    },
    isSuperAdmin(state) {
      return Number(state.adminInfo.rating) === 1;
    },
    getAppListGetter(state) {
      return state.appList;
    },
    hasPermission: (state) => (permissionKey) => {
      if (Number(state.adminInfo.rating) === 1) {
        return true;
      }
      return hasPermission(state.permissionKeys, permissionKey);
    },
    hasAnyPermission: (state) => (permissionList) => {
      if (Number(state.adminInfo.rating) === 1) {
        return true;
      }
      return hasAnyPermission(state.permissionKeys, permissionList);
    },
    registeredRoutePaths(state) {
      return getRegisteredRoutePaths(state.menuTree);
    },
    firstReachablePath(state) {
      return resolveDefaultPath(state.menuTree, state.defaultPath);
    },
    fallbackPath(state) {
      const firstMenuPath = findFirstMenuPath(state.menuTree);
      return (
        resolveDefaultPath(state.menuTree, state.defaultPath) ||
        firstMenuPath ||
        AUTH_ROUTE_FALLBACK
      );
    },
  },
});

export default store;
